'use client'
// ShareMenu — reusable social share buttons for deals.
// compact=true  → small inline buttons (used in DealCard popover)
// compact=false → full-width flex row (used in deal detail page)
import { useState } from 'react'

const WA_ICON_SM = (
  <svg viewBox="0 0 24 24" width="12" height="12" className="fill-current shrink-0" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.525 5.845L.057 23.547a.5.5 0 0 0 .609.61l5.765-1.508A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.686-.505-5.218-1.385l-.374-.22-3.878 1.016 1.032-3.78-.242-.389A9.956 9.956 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
  </svg>
)
const X_ICON_SM = (
  <svg viewBox="0 0 24 24" width="12" height="12" className="fill-current shrink-0" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
)
const TG_ICON_SM = (
  <svg viewBox="0 0 24 24" width="12" height="12" className="fill-current shrink-0" aria-hidden="true">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
)
const COPY_ICON_SM = (
  <svg viewBox="0 0 24 24" width="12" height="12" className="stroke-current fill-none shrink-0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
)

export default function ShareMenu({ dealUrl, shareText, compact = false, onClose, refCode = null }) {
  const [copied, setCopied] = useState(false)
  const shareUrl = refCode ? `${dealUrl}?ref=${refCode}` : dealUrl

  const waHref       = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`
  const twitterHref  = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
  const telegramHref = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`

  function handleCopy(e) {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    navigator.clipboard.writeText(shareUrl).catch(() => {})
    window.plausible?.('ShareDeal', { props: { method: 'copy' } })
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function track(method, e) {
    e?.stopPropagation?.()
    window.plausible?.('ShareDeal', { props: { method } })
  }

  if (compact) {
    return (
      <div
        className="flex gap-1.5 flex-wrap items-center"
        onClick={(e) => { e.preventDefault(); e.stopPropagation() }}
      >
        <a href={waHref} target="_blank" rel="noopener noreferrer"
          onClick={(e) => track('whatsapp', e)}
          className="flex items-center gap-1.5 text-xs bg-green-500 hover:bg-green-600 text-white px-2.5 py-1 rounded-lg transition-colors"
        >
          {WA_ICON_SM} WhatsApp
        </a>
        <a href={twitterHref} target="_blank" rel="noopener noreferrer"
          onClick={(e) => track('twitter', e)}
          className="flex items-center gap-1.5 text-xs bg-black hover:bg-gray-800 text-white px-2.5 py-1 rounded-lg transition-colors"
        >
          {X_ICON_SM} X
        </a>
        <a href={telegramHref} target="_blank" rel="noopener noreferrer"
          onClick={(e) => track('telegram', e)}
          className="flex items-center gap-1.5 text-xs bg-sky-500 hover:bg-sky-600 text-white px-2.5 py-1 rounded-lg transition-colors"
        >
          {TG_ICON_SM} Telegram
        </a>
        <button type="button" onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1 rounded-lg transition-colors"
        >
          {COPY_ICON_SM} {copied ? 'Copied!' : 'Copy link'}
        </button>
        {onClose && (
          <button type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose() }}
            className="ml-auto text-xs text-gray-400 hover:text-gray-600 px-1 py-1"
            aria-label="Close share menu"
          >
            ✕
          </button>
        )}
      </div>
    )
  }

  // Full-size row (deal detail page)
  return (
    <div className="flex gap-2 flex-wrap">
      <a href={waHref} target="_blank" rel="noopener noreferrer"
        onClick={() => track('whatsapp')}
        className="plausible-event-name=ShareDeal flex-1 flex items-center justify-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg py-2 transition-colors"
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
          <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.525 5.845L.057 23.547a.5.5 0 0 0 .609.61l5.765-1.508A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.686-.505-5.218-1.385l-.374-.22-3.878 1.016 1.032-3.78-.242-.389A9.956 9.956 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
        </svg>
        WhatsApp
      </a>
      <a href={twitterHref} target="_blank" rel="noopener noreferrer"
        onClick={() => track('twitter')}
        className="plausible-event-name=ShareDeal flex-1 flex items-center justify-center gap-1.5 bg-black hover:bg-gray-800 text-white text-sm font-medium rounded-lg py-2 transition-colors"
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
        Twitter / X
      </a>
      <a href={telegramHref} target="_blank" rel="noopener noreferrer"
        onClick={() => track('telegram')}
        className="plausible-event-name=ShareDeal flex-1 flex items-center justify-center gap-1.5 bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium rounded-lg py-2 transition-colors"
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
        Telegram
      </a>
      <button type="button" onClick={handleCopy}
        className="flex-1 flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg py-2 transition-colors"
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4 stroke-current fill-none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="9" y="9" width="13" height="13" rx="2"/>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
        </svg>
        {copied ? 'Copied!' : 'Copy link'}
      </button>
    </div>
  )
}
